(function () {
  'use strict';

  var STAGES = ['Ordered', 'Shipped', 'In transit', 'Out for delivery', 'Delivered'];

  // Grayscale base so our own red/black markers are the only colour on the map.
  var MAP_STYLE = [
    { elementType: 'geometry', stylers: [{ color: '#f4f4f4' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6b' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d0d0d0' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#fbfbfb' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dcdcdc' }] }
  ];

  var mapsPromise = null;
  var mapSeq = 0;

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function formatDay(isoString) {
    if (!isoString) return '';
    var date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function formatLongDay(isoString) {
    if (!isoString) return '';
    var date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  }

  function formatTime(isoString) {
    if (!isoString) return '';
    var date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function formatMoney(amount, currency) {
    var value = parseFloat(amount);
    if (isNaN(value)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value);
    } catch (err) {
      return value.toFixed(2);
    }
  }

  /**
   * The theme and the Worker deploy separately, so a freshly published theme can
   * briefly be talking to an older Worker. Accept either field name rather than
   * silently showing "Shipped" with no history when that happens.
   */
  function normalizeFulfillments(fulfillments) {
    return fulfillments.map(function (f) {
      if (!f.tracking && f.chitchats) f.tracking = f.chitchats;
      return f;
    });
  }

  function primaryShipment(fulfillments) {
    for (var i = 0; i < fulfillments.length; i++) {
      if (fulfillments[i].tracking) return fulfillments[i];
    }
    return fulfillments[0] || null;
  }

  function isMovementEvent(event) {
    return (
      event.status === 'in_transit' ||
      event.status === 'out_for_delivery' ||
      /in transit|out for delivery|departed|left our facility|arrived/i.test(event.title || '')
    );
  }

  function isDeliveredEvent(event) {
    return event.status === 'delivered' || /delivered/i.test(event.title || '');
  }

  function isOutForDeliveryEvent(event) {
    return event.status === 'out_for_delivery' || /out for delivery/i.test(event.title || '');
  }

  /**
   * The public tracking feed reports many events with a null status, and the
   * newest event isn't always the furthest along (a carrier can create its label
   * after the parcel already moved), so movement anywhere in the history counts.
   */
  function deriveStatus(shipment) {
    if (!shipment) return { label: 'Order confirmed', stage: 0, delivered: false };

    var cc = shipment.tracking;
    if (!cc) return { label: 'Shipped', stage: 1, delivered: false };

    var events = cc.events || [];
    var latest = events.length ? events[0] : null;

    if (cc.resolvedAt || cc.resolution === 'delivery_confirmed' || (latest && isDeliveredEvent(latest))) {
      return { label: 'Delivered', stage: 4, delivered: true };
    }

    if (latest && isOutForDeliveryEvent(latest)) {
      return { label: 'Out for delivery', stage: 3, delivered: false };
    }

    if (events.some(isMovementEvent)) return { label: 'On its way', stage: 2, delivered: false };
    if (!latest) return { label: 'Shipped', stage: 1, delivered: false };

    return { label: 'Preparing to ship', stage: 1, delivered: false };
  }

  /**
   * Date shown under each step. Events arrive newest-first and each slot is only
   * filled once, so a step shows its most recent update rather than the first
   * time the parcel hit that stage.
   */
  function stageDates(order, shipment) {
    var cc = shipment ? shipment.tracking : null;
    var events = (cc && cc.events) || [];
    var dates = [order.createdAt, shipment ? shipment.createdAt : null, null, null, null];

    if (cc && cc.shipDate && !dates[1]) dates[1] = cc.shipDate;

    events.forEach(function (event) {
      if (!dates[4] && isDeliveredEvent(event)) dates[4] = event.timestamp;
      if (!dates[3] && isOutForDeliveryEvent(event)) dates[3] = event.timestamp;
      if (!dates[2] && isMovementEvent(event) && !isOutForDeliveryEvent(event)) dates[2] = event.timestamp;
    });

    if (cc && cc.resolvedAt) dates[4] = cc.resolvedAt;
    return dates;
  }

  function renderStatusCard(order, shipment, supportUrl) {
    var derived = deriveStatus(shipment);
    var cc = shipment ? shipment.tracking : null;
    var latest = cc && cc.events && cc.events.length ? cc.events[0] : null;
    var dates = stageDates(order, shipment);

    var html = '<div class="to-panel to-status' + (derived.delivered ? ' to-status--delivered' : '') + '">';

    html += '<div class="to-status__top">';
    html += '<div>';
    html += '<div class="to-eyebrow">Order ' + escapeHtml(order.name) + '</div>';

    if (derived.delivered) {
      html +=
        '<h2 class="to-status__headline">' +
        '<span class="to-status__check" aria-hidden="true">&#10003;</span>Delivered!</h2>' +
        '<p class="to-status__sub">Your order made it. Thanks for riding with us — we hope it was worth the wait.</p>';
    } else {
      html += '<h2 class="to-status__headline">' + escapeHtml(derived.label) + '</h2>';
      if (cc && cc.estimatedDeliveryAt) {
        html +=
          '<p class="to-status__sub">Estimated arrival: <strong>' +
          escapeHtml(formatLongDay(cc.estimatedDeliveryAt)) +
          '</strong></p>';
      } else {
        html += '<p class="to-status__sub">Placed ' + escapeHtml(formatLongDay(order.createdAt)) + '</p>';
      }
    }

    html += '</div>';

    if (supportUrl) {
      html += '<a class="to-btn to-btn--ghost" href="' + escapeHtml(supportUrl) + '">Contact support</a>';
    }

    html += '</div>';

    html += '<ol class="to-steps">';
    STAGES.forEach(function (name, index) {
      var state = index < derived.stage ? 'done' : index === derived.stage ? 'current' : 'todo';
      html +=
        '<li class="to-steps__step to-steps__step--' +
        state +
        '" style="--toStep:' +
        index +
        '">' +
        '<span class="to-steps__dot">' +
        (state === 'done' ? '<span class="to-steps__tick" aria-hidden="true">&#10003;</span>' : '') +
        '</span>' +
        '<span class="to-steps__name">' +
        escapeHtml(name) +
        '</span>' +
        '<span class="to-steps__date">' +
        escapeHtml(dates[index] ? formatDay(dates[index]) : '') +
        '</span>' +
        '</li>';
    });
    html += '</ol>';

    if (latest) {
      html += '<div class="to-callout">';
      html += '<div class="to-callout__label">Latest update</div>';
      html += '<div class="to-callout__title">' + escapeHtml(latest.title) + '</div>';
      if (latest.subtitle) {
        html += '<div class="to-callout__text">' + escapeHtml(latest.subtitle) + '</div>';
      }
      if (latest.location) {
        html += '<div class="to-callout__meta">' + escapeHtml(latest.location) + '</div>';
      }
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Only ever built from sanitized event locations, so a leg that was hidden for
   * being Canadian can never be plotted on the map.
   */
  function mapLocations(order, shipment) {
    var cc = shipment ? shipment.tracking : null;
    var events = (cc && cc.events) || [];
    var current = null;

    for (var i = 0; i < events.length; i++) {
      if (events[i].location) {
        current = events[i].location;
        break;
      }
    }

    var dest = order.destination || {};
    var destination = dest.city ? dest.city + (dest.province ? ', ' + dest.province : '') : null;

    return { current: current, destination: destination };
  }

  function loadMaps(apiKey) {
    if (mapsPromise) return mapsPromise;

    mapsPromise = new Promise(function (resolve, reject) {
      if (window.google && window.google.maps) return resolve(window.google.maps);

      var callbackName = 'trackOrderMapsReady';
      window[callbackName] = function () {
        resolve(window.google.maps);
      };

      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&callback=' +
        callbackName;
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return mapsPromise;
  }

  function geocode(geocoder, address) {
    return new Promise(function (resolve) {
      geocoder.geocode({ address: address }, function (results, status) {
        resolve(status === 'OK' && results && results[0] ? results[0].geometry.location : null);
      });
    });
  }

  /**
   * Quadratic bezier between the two points, bowed out perpendicular to the
   * line, so the link reads as a flight-path style arc rather than a ruler line.
   */
  function arcBetween(from, to) {
    var lat1 = from.lat();
    var lng1 = from.lng();
    var lat2 = to.lat();
    var lng2 = to.lng();

    var dLat = lat2 - lat1;
    var dLng = lng2 - lng1;
    var curve = 0.2;

    var ctrlLat = (lat1 + lat2) / 2 + dLng * curve;
    var ctrlLng = (lng1 + lng2) / 2 - dLat * curve;

    var points = [];
    var steps = 64;
    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      var mt = 1 - t;
      points.push({
        lat: mt * mt * lat1 + 2 * mt * t * ctrlLat + t * t * lat2,
        lng: mt * mt * lng1 + 2 * mt * t * ctrlLng + t * t * lng2
      });
    }

    return points;
  }

  function markerIcon(maps, color) {
    return {
      path: maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 3
    };
  }

  function drawMap(el, places, apiKey) {
    loadMaps(apiKey)
      .then(function (maps) {
        var map = new maps.Map(el, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          styles: MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative'
        });

        var geocoder = new maps.Geocoder();
        var lookups = [
          places.current ? geocode(geocoder, places.current) : Promise.resolve(null),
          places.destination ? geocode(geocoder, places.destination) : Promise.resolve(null)
        ];

        return Promise.all(lookups).then(function (points) {
          var current = points[0];
          var destination = points[1];
          var bounds = new maps.LatLngBounds();
          var plotted = 0;

          if (current) {
            new maps.Marker({
              position: current,
              map: map,
              icon: markerIcon(maps, '#cf2424'),
              title: places.current,
              zIndex: 2
            });
            bounds.extend(current);
            plotted++;
          }

          if (destination) {
            new maps.Marker({
              position: destination,
              map: map,
              icon: markerIcon(maps, '#14803c'),
              title: places.destination,
              zIndex: 1
            });
            bounds.extend(destination);
            plotted++;
          }

          // A simple curved link between the two points - deliberately not a
          // driving route, since a parcel's path isn't a car trip and the travel
          // time Google shows for one is meaningless here.
          if (current && destination) {
            var arc = arcBetween(current, destination);
            new maps.Polyline({
              path: arc,
              geodesic: false,
              strokeColor: '#cf2424',
              strokeOpacity: 0.9,
              strokeWeight: 3,
              map: map
            });
            arc.forEach(function (point) {
              bounds.extend(point);
            });
          }

          if (plotted === 2) {
            map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
          } else if (plotted === 1) {
            map.setCenter(bounds.getCenter());
            map.setZoom(8);
          }
        });
      })
      .catch(function () {
        el.classList.add('to-map__frame--failed');
      });
  }

  function renderMap(order, shipment, apiKey, pending) {
    if (!apiKey) return '';

    var places = mapLocations(order, shipment);
    if (!places.current && !places.destination) return '';

    var id = 'TrackOrderMap-' + ++mapSeq;
    pending.push({ id: id, places: places, apiKey: apiKey });

    var html = '<div class="to-panel to-map">';
    html += '<div class="to-panel__head"><h3 class="to-panel__title">Live tracking</h3></div>';
    html += '<div class="to-map__frame" id="' + id + '"></div>';

    var legend = [];
    if (places.current) {
      legend.push(
        '<span class="to-map__key"><span class="to-map__pin to-map__pin--current"></span>' +
          escapeHtml(places.current) +
          '</span>'
      );
    }
    if (places.destination) {
      legend.push(
        '<span class="to-map__key"><span class="to-map__pin to-map__pin--dest"></span>' +
          escapeHtml(places.destination) +
          '</span>'
      );
    }
    if (legend.length) html += '<div class="to-map__legend">' + legend.join('') + '</div>';

    html += '</div>';
    return html;
  }

  /**
   * Carrier scans can lag a couple of days behind, so when none are available
   * yet the history is built from what the order itself already tells us.
   */
  function fallbackEvents(order, shipment) {
    var events = [];

    if (shipment && shipment.createdAt) {
      events.push({
        title: 'Shipped',
        subtitle: 'Tracking updates usually start appearing within 2 business days.',
        location: null,
        timestamp: shipment.createdAt
      });
    }

    if (order.createdAt) {
      events.push({ title: 'Order placed', subtitle: null, location: null, timestamp: order.createdAt });
    }

    return events;
  }

  function renderHistory(events, delivered) {
    var html = '<div class="to-panel to-history' + (delivered ? ' to-history--delivered' : '') + '">';
    html += '<div class="to-panel__head"><h3 class="to-panel__title">Delivery updates</h3></div>';
    html += '<ol class="to-timeline">';

    events.forEach(function (event, index) {
      html +=
        '<li class="to-timeline__item' +
        (index === 0 ? ' to-timeline__item--latest' : '') +
        '" style="--toRow:' +
        index +
        '">' +
        '<div class="to-timeline__when">' +
        '<span class="to-timeline__date">' +
        escapeHtml(formatDay(event.timestamp)) +
        '</span>' +
        '<span class="to-timeline__time">' +
        escapeHtml(formatTime(event.timestamp)) +
        '</span>' +
        '</div>' +
        '<div class="to-timeline__marker"></div>' +
        '<div class="to-timeline__body">' +
        '<div class="to-timeline__title">' +
        escapeHtml(event.title) +
        '</div>';

      if (event.subtitle) {
        html += '<div class="to-timeline__sub">' + escapeHtml(event.subtitle) + '</div>';
      }
      if (event.location) {
        html += '<div class="to-timeline__meta">' + escapeHtml(event.location) + '</div>';
      }

      html += '</div></li>';
    });

    html += '</ol></div>';
    return html;
  }

  function renderOrderDetails(order) {
    var items = order.lineItems || [];
    if (!items.length) return '';

    var html = '<div class="to-panel to-items">';
    html += '<div class="to-panel__head"><h3 class="to-panel__title">Products</h3>';
    html += '<span class="to-panel__meta">Placed ' + escapeHtml(formatDay(order.createdAt)) + '</span></div>';
    html += '<ul class="to-items__list">';

    items.forEach(function (item) {
      // Only products we resolved a handle for become links; the rest stay as
      // plain rows rather than dead anchors.
      var isLink = Boolean(item.url);
      var tag = isLink ? 'a' : 'div';

      html += '<li class="to-items__item">';
      html +=
        '<' +
        tag +
        ' class="to-items__row' +
        (isLink ? ' to-items__row--link' : '') +
        '"' +
        (isLink ? ' href="' + escapeHtml(item.url) + '"' : '') +
        '>';

      if (item.image) {
        html +=
          '<div class="to-items__thumb"><img src="' +
          escapeHtml(item.image) +
          '" alt="' +
          escapeHtml(item.title) +
          '" loading="lazy"></div>';
      } else {
        html += '<div class="to-items__thumb to-items__thumb--empty" aria-hidden="true"></div>';
      }

      html += '<div class="to-items__info">';
      html += '<div class="to-items__title">' + escapeHtml(item.title) + '</div>';

      var meta = [];
      if (item.variantTitle) meta.push(item.variantTitle);
      meta.push('Qty: ' + item.quantity);
      html += '<div class="to-items__meta">' + escapeHtml(meta.join('  ·  ')) + '</div>';
      html += '</div>';

      var money = formatMoney(item.price, order.currency);
      if (money) html += '<div class="to-items__price">' + escapeHtml(money) + '</div>';

      html += '</' + tag + '></li>';
    });

    html += '</ul>';
    html +=
      '<div class="to-note"><span class="to-note__icon" aria-hidden="true">&#10003;</span>' +
      '<span>There are no additional charges, duties, or tariffs due for this shipment.</span></div>';
    html += '</div>';
    return html;
  }

  function renderResults(container, data, settings) {
    var order = data.order || {};
    var fulfillments = normalizeFulfillments(data.fulfillments || []);
    var shipment = primaryShipment(fulfillments);
    var cc = shipment ? shipment.tracking : null;
    var events = cc && cc.events && cc.events.length ? cc.events : fallbackEvents(order, shipment);
    var derived = deriveStatus(shipment);
    var pendingMaps = [];

    var html = renderStatusCard(order, shipment, settings.supportUrl);

    html += '<div class="to-grid">';
    html += '<div class="to-grid__col">' + renderHistory(events, derived.delivered) + '</div>';
    html += '<div class="to-grid__col">';
    html += renderOrderDetails(order);
    html += renderMap(order, shipment, settings.mapsApiKey, pendingMaps);
    html += '</div>';
    html += '</div>';

    container.innerHTML = html;
    container.hidden = false;

    pendingMaps.forEach(function (job) {
      var el = document.getElementById(job.id);
      if (el) drawMap(el, job.places, job.apiKey);
    });
  }

  function init(root) {
    var sectionId = root.id.replace('TrackOrder-', '');
    var form = root.querySelector('#TrackOrderForm-' + sectionId);
    var errorsEl = root.querySelector('#TrackOrderErrors-' + sectionId);
    var resultsEl = root.querySelector('#TrackOrderResults-' + sectionId);
    var submitBtn = root.querySelector('#TrackOrderSubmit-' + sectionId);
    var submitText = submitBtn ? submitBtn.querySelector('.track-order__submit-text') : null;
    var settings = (window.trackOrderSettings && window.trackOrderSettings[sectionId]) || {};
    var endpoint = settings.endpoint;

    if (!form) return;

    function showError(message) {
      errorsEl.textContent = message;
      errorsEl.hidden = false;
    }

    function clearError() {
      errorsEl.textContent = '';
      errorsEl.hidden = true;
    }

    function setLoading(isLoading) {
      if (!submitBtn) return;
      submitBtn.disabled = isLoading;
      root.classList.toggle('track-order--loading', isLoading);
      if (submitText) {
        submitText.textContent = isLoading ? 'Looking up your order…' : 'Track my order';
      }
    }

    function runLookup(orderNumber, email, scrollToResults) {
      clearError();
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';

      if (!orderNumber || !email) {
        showError('Please enter both your order number and email address.');
        return;
      }

      if (!endpoint) {
        showError('Order tracking is not configured yet. Please contact support.');
        return;
      }

      setLoading(true);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber, email: email })
      })
        .then(function (response) {
          return response.json().then(function (body) {
            return { ok: response.ok, body: body };
          });
        })
        .then(function (result) {
          setLoading(false);
          if (!result.ok) {
            showError(
              (result.body && result.body.error) ||
                'We couldn’t find an order matching that email and order number. Please double-check and try again.'
            );
            return;
          }

          // Orders shipping outside the US go straight to the carrier's own page.
          var redirect = result.body && result.body.redirectUrl;
          if (redirect && /^https:\/\//.test(redirect)) {
            resultsEl.innerHTML = '<div class="to-panel">Taking you to your tracking page…</div>';
            resultsEl.hidden = false;
            window.location.href = redirect;
            return;
          }

          renderResults(resultsEl, result.body, settings);

          if (scrollToResults && resultsEl.scrollIntoView) {
            resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function () {
          setLoading(false);
          showError('Something went wrong while looking up your order. Please try again in a moment.');
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      runLookup(form.order_number.value.trim(), form.email.value.trim(), true);
    });

    // Prefill from ?order=1234&email=someone@example.com so order confirmation
    // emails can link straight to a customer's tracking. Both present runs the
    // lookup right away; only one just fills that field in.
    var params = new URLSearchParams(window.location.search);
    var orderParam = (params.get('order') || '').trim();
    var emailParam = (params.get('email') || '').trim();

    if (orderParam) form.order_number.value = orderParam;
    if (emailParam) form.email.value = emailParam;
    if (orderParam && emailParam) runLookup(orderParam, emailParam, false);
  }

  document.querySelectorAll('.track-order').forEach(init);
})();
